/**
 * Fantasy Console 0 - Play Tab
 *
 * Full-screen console view for playing the game.
 */

import { useRef, useEffect } from 'react'
import { useCart } from '../../context/CartContext'
import { useConsole } from '../../context/ConsoleContext'
import { ConsoleWrapper, ConsoleWrapperRef } from '../Console/ConsoleWrapper'
import styles from './PlayTab.module.css'

export function PlayTab() {
  const { cart, activeView } = useCart()
  const { registerActiveConsole, unregisterActiveConsole } = useConsole()
  const consoleRef = useRef<ConsoleWrapperRef>(null)

  // @ts-ignore: username is a provided field on carts from the carts_with_stats view from Supabase
  const author = cart.username ?? cart.metadata.author

  // Register console for global keyboard shortcuts when play view is active
  useEffect(() => {
    if (activeView === 'play') {
      registerActiveConsole({
        reload: () => consoleRef.current?.reload(),
        pauseResume: () => consoleRef.current?.pauseResume(),
        reset: () => consoleRef.current?.reset(),
      })
      return () => unregisterActiveConsole()
    }
  }, [activeView, registerActiveConsole, unregisterActiveConsole])

  return (
    <div className={styles.container}>
      <div className={styles.consoleArea}>
        <ConsoleWrapper
          ref={consoleRef}
          size='xLarge'
          showControls={true}
          autoReloadOnCodeChange={false}
        />
      </div>

      <div className={styles.cartInfo}>
        <h2 className={styles.title}>{cart.metadata.title}</h2>
        {author && <p className={styles.author}>by {author}</p>}
        {cart.metadata.description && (
          <p className={styles.description}>{cart.metadata.description}</p>
        )}
      </div>

      <div className={styles.controls}>
        <h3 className={styles.controlsTitle}>Controls</h3>
        <div className={styles.controlsList}>
          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>D-Pad</span>
            <div className={styles.keys}>
              <kbd>Arrow Keys / WASD</kbd>
            </div>
          </div>
          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>B Button</span>
            <div className={styles.keys}>
              <kbd>Z / J</kbd>
            </div>
          </div>
          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>A Button</span>
            <div className={styles.keys}>
              <kbd>X / K</kbd>
            </div>
          </div>
          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>Start</span>
            <div className={styles.keys}>
              <kbd>Space / Enter</kbd>
            </div>
          </div>
          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>Select</span>
            <div className={styles.keys}>
              <kbd>Shift</kbd>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
